import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Container, Box, Chip, useMediaQuery } from '@material-ui/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Autoplay } from 'swiper';
import ReactPlayer from 'react-player/lazy';
import Text from '../components/Text';
import CtaButton from '../components/CtaButton';
import Icon from '../components/Icon';
import { GTMEvent } from '../utils/gtm';
import { useTranslation } from 'react-i18next';
import FluidImage from '../components/FluidImage';
import 'swiper/swiper-bundle.css'
import Module from '../components/Module';

// Initialize Swiper modules
SwiperCore.use([Navigation, Autoplay]);

// Utility function to get from localStorage
const getLocaleStorage = (key) => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(key);
  }
  return null;
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    zIndex: 20,
    width: '100%',
    overflow: 'hidden',
  },
  heroSlideContainer: {
    position: 'relative',
    height: 'min(56.25vw, 720px, 100vh)',
    width: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      height: '84vh !important',
    },
    [theme.breakpoints.only('sm')]: {
      '@media only screen and (orientation: landscape)': {
        height: '500px',
      },
    },
    [theme.breakpoints.between('sm', 'md')]: {
      '@media only screen and (orientation: landscape)': {
        height: '470px',
      },
    },
  },
  backgroundMediaContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  videoBackground: {
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  reactPlayer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '100%',
    minHeight: '100%',
    width: '100% !important',
    height: '100vh !important',
    objectFit: 'cover',
    backgroundSize: 'cover',
    zIndex: 1,
    '@media (min-aspect-ratio: 16/9)': {
      width: '100% !important',
      height: '150% !important',
    },
    '@media (min-aspect-ratio: 21/9)': {
      width: '100% !important',
      height: '200% !important',
    },
    [theme.breakpoints.down(1299)]: {
      width: '177.78vh !important',
      height: '100% !important',
    },
  },
  slideBackground: {
    height: '100%',
    '& img': {
      width: '100% !important',
      height: '100% !important',
      maxWidth: '100% !important',
      objectFit: 'cover !important',
    }
  },
  slideContentContainer: {
    position: 'relative',
    height: '100%',
    zIndex: 2,
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    [theme.breakpoints.down('md')]: {
      maxWidth: '960px',
      padding: '0 16px !important',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
  },
  videoControlBtn: {
    position: 'absolute',
    bottom: 24,
    left: 24,
    width: 40,
    height: 40,
    cursor: 'pointer',
    zIndex: 5,
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    [theme.breakpoints.down('xs')]: {
      bottom: 12,
      right: 8,
      width: 24,
      height: 24,
    },
  },
  legalText: {
    position: 'absolute',
    bottom: 80,
    fontFamily: 'Poppins',
    fontSize: 16,
    marginLeft: '28%',
    zIndex: 2,
    '@media (min-width: 1920px)': {
      bottom: '10px !important',
      fontSize: '20px !important',
      marginLeft: '20% !important',
    },
  },
  legalTextMobile: {
    position: 'absolute',
    bottom: '33%',
    fontFamily: 'Poppins',
    fontSize: 15,
    zIndex: 2,
    paddingLeft: 16,
    paddingRight: 16,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  slideDescriptionContainer: {
    maxWidth: 515,
    border: 'unset',
    zIndex: 3,
    position: 'absolute',
    top: 100,
    color: '#fbfbfb',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      left: 0,
      top: '0 !important',
      width: '100%',
      padding: '16px 16px 60px 16px !important',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transform: 'unset !important',
    },
    [theme.breakpoints.down('md')]: {
      top: '50%',
      transform: 'translateY(-50%)',
      maxWidth: '100%',
    },
    [theme.breakpoints.only('sm')]: {
      '@media only screen and (orientation: landscape)': {
        left: 50,
        width: '47%',
        border: 'unset',
        zIndex: 99,
        position: 'absolute',
        top: 70,
      },
    },
    '@media screen and (width: 1024px) and (height: 1366px)': {
      width: '50%',
      top: 0,
    },
  },
  slideLogo: {
    maxWidth: '100%',
    '&.logoCenter': {
      maxWidth: 515,
      zIndex: 99,
      position: 'absolute',
      left: '35%',
      top: '14%',
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
      marginRight: 'auto',
    },
  },
  noChip: {
    marginBottom: '-25px',
  },
  logoNarrowDesktop: {
    '& img': {
      width: 'auto',
      maxHeight: '200px !important',
      objectFit: 'contain',
    },
  },
  slideForeground: {
    marginTop: 16,
    marginBottom: 23,
    maxHeight: 100,
    height: '100%',
    display: 'flex !important',
    alignItems: 'center',
    justifyContent: 'start',
    '& img': {
      objectFit: 'cover',
      maxHeight: 100,
      height: '100%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      '@media only screen and (orientation: landscape)': {
        maxWidth: 295,
        height: 97,
        marginTop: 10,
        marginBottom: 5,
      },
    },
  },
  slideLeft: {
    height: '100%',
  },
  slideNew: {
    minHeight: 23,
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxHeight: '15%',
    maxWidth: 250,
    objectFit: 'contain',
    [theme.breakpoints.down('xs')]: {
      marginTop: 2,
      marginRight: '40%',
      marginLeft: 8,
    },
  },
  newArrivals: {
    position: 'absolute',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: 'white',
    border: 'none',
    cursor: 'default',
    height: 19,
    display: 'inline-flex',
    outline: 0,
    padding: 4,
    fontSize: 12,
    boxSizing: 'border-box',
    transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    alignItems: 'center',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.27,
    whiteSpace: 'nowrap',
    borderRadius: 0,
    verticalAlign: 'middle',
    justifyContent: 'center',
    textDecoration: 'none',
    backgroundColor: '#ff2765',
  },
  slideTitle: {
    fontWeight: 'bold',
    fontSize: 48,
    fontFamily: '"Poppins", sans-serif',
    lineHeight: '64px',
    letterSpacing: '0em',
    color: '#fbfbfb',
    margin: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px !important',
      fontWeight: '800 !important',
      lineHeight: '33px !important',
    },
    [theme.breakpoints.only('sm')]: {
      '@media only screen and (orientation: landscape)': {
        fontSize: 22,
        fontWeight: 800,
        lineHeight: '33px',
      },
    },
    [theme.breakpoints.down(848)]: {
      fontSize: 32,
    },
    '@media screen and (width: 1024px) and (height: 1366px)': {
      fontWeight: 'bold',
      fontSize: 48,
      fontFamily: '"Poppins", sans-serif',
      lineHeight: '50px',
      letterSpacing: '0em',
      margin: 0,
    },
  },
  slideSubtitle: {
    marginTop: 16,
    marginBottom: 30,
    fontSize: 24,
    fontWeight: 600,
    lineHeight: '36px',
    letterSpacing: '0em',
    fontFamily: '"Poppins", sans-serif',
    [theme.breakpoints.down('xs')]: {
      fontSize: '16px !important',
      lineHeight: '24px !important',
      textAlign: 'center !important',
    },
    [theme.breakpoints.only('sm')]: {
      '@media only screen and (orientation: landscape)': {
        fontSize: 16,
        lineHeight: '24px',
        textAlign: 'center',
      },
    },
    [theme.breakpoints.between('sm', 'md')]: {
      '@media only screen and (orientation: landscape)': {
        fontSize: 16,
        lineHeight: '24px',
        textAlign: 'center',
        display: 'none',
      },
    },
    [theme.breakpoints.down(848)]: {
      fontSize: 24,
    },
  },
  ctaLabel: {
    fontWeight: 600,
    fontSize: 18,
    lineHeight: '27px',
  },
  ribbonSection: {
    position: 'absolute',
    top: 24,
    right: 0,
    zIndex: 10,
    background: '#FF2765',
    padding: 8,
    marginTop: 40,
    [theme.breakpoints.up('md')]: {
      padding: '8px 16px',
      marginTop: 50,
    },
    '& .text': {
      fontFamily: '"Poppins", sans-serif',
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 'unset !important',
      color: '#fff',
      [theme.breakpoints.up('md')]: {
        fontSize: 16,
      },
    },
  },
  navigation: {
    position: 'relative',
    bottom: 'auto',
    right: 'auto',
    zIndex: 5,
    color: '#fff',
    gap: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      bottom: 24,
      right: 24,
    },
    '& .prevRotate': {
      transform: 'rotate(-180deg)',
    },
  },
  navigationMobile: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
    }
  },
  mySwiperHeroBannerVideos: {
    height: '100%',
    position: 'relative',
    width: '100%',
  },
  mb4p: {
    marginBottom: 4,
  },
  mb22p: {
    marginBottom: 22,
  },
  mb0: {
    marginBottom: '0px !important',
  },
  mb4: {
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      '@media only screen and (orientation: landscape)': {
        marginBottom: 16,
      },
    },
  },
  jCenter: {
    justifyContent: 'center',
  },
  dFlex: {
    display: 'flex',
  },
  textCenter: {
    textAlign: 'center',
  },
  btnSmall: {
    [theme.breakpoints.down('xs')]: {
      borderRadius: 4,
      height: '43px !important',
    },
    [theme.breakpoints.only('sm')]: {
      '@media only screen and (orientation: landscape)': {
        fontSize: '16px !important',
        height: '56px !important',
        margin: '0 auto !important',
        maxWidth: '100% !important',
        width: '100% !important',
      },
    },
  },
  marginIpad: {
    '@media screen and (width: 1024px) and (height: 1366px)': {
      marginBottom: '18px !important',
    },
  },
  w100: {
    width: '100%',
  },
  h100: {
    height: '100%',
  },
  dNone: {
    display: 'none',
  },
  dBlock: {
    display: 'block',
  },
  dMdNone: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  dMdFlex: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  navigationText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    '& p': {
      fontWeight: 600,
    }
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',

    '&:hover': {
      transform: 'scale(1.1)',
      transition: 'transform 0.3s ease',
    }
  },
  icon24: {
    width: 24,
    height: 24,
  },
  textDecoration: {
    textDecoration: 'none',
  },
  container: {
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  ctaButton: {
    borderRadius: 4,
    fontSize: 16,
    fontWeight: 600,
    padding: '12px 24px',
    '&:hover': {
      backgroundColor: '#be7425',
    },
  },
}));

const HeroBannerSlidingVideosModule = props => {
  const {item, info} = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const {t} = useTranslation();

  const [indexSwiper, setIndexSwiper] = useState(0);
  const [isBrowser, setIsBrowser] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const playerRefs = useRef([]);
  const firstModulePage = getLocaleStorage('first_module_page');
  const currentSlide = useMemo(() => String(indexSwiper + 1).padStart(2, '0'), [indexSwiper]);
  const totalSlides = useMemo(() => String(item?.field_value?.length || 0).padStart(2, '0'), [item?.field_value?.length]);

  useEffect(() => {
    if (item?.field_value) {
      playerRefs.current = item.field_value.map((_, i) => playerRefs.current[i] || null);
    }
  }, [item?.field_value]);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const toggleMute = () => setIsMuted(prev => !prev);

  const handleSlideChange = swiperCore => {
    const {realIndex} = swiperCore;

    const normalizeIndex = i => {
      if (!item?.field_value?.length) return 0;
      if (i < 0) return item.field_value.length - 1;
      if (i > item.field_value.length - 1) return 0;
      return i;
    };

    playerRefs.current.forEach((ref, i) => {
      try {
        if (ref && i !== realIndex) {
          ref.seekTo(0, 'seconds');
        }
      } catch (e) {
        console.error('Error seeking video:', e);
      }
    });

    setIndexSwiper(normalizeIndex(realIndex));
  };

  if (!item?.field_value || !item.field_value.length) return null;

  return (
    isBrowser && <Module className={classes.root} variant="full" noMargin>
      <Swiper
        loop={item.field_value.length > 1}
        autoplay={
          item.field_value.length > 1
            ? {delay: 8000, disableOnInteraction: false}
            : false
        }
        className={classes.mySwiperHeroBannerVideos}
        allowTouchMove
        slidesPerView={1}
        spaceBetween={0}
        centeredSlides
        navigation={isBrowser ? {
          nextEl: '#heroBannerVideosNext',
          prevEl: '#heroBannerVideosPrev',
        } : null}
        onSlideChange={handleSlideChange}
      >
        {item.field_value.map((element, index) => {
          const videoUrlDesktop = element?.field_value?.video_url_desktop?.field_value;
          const videoUrlMobile = element?.field_value?.video_url_mobile?.field_value;
          const videoUrl = isMobile && videoUrlMobile ? videoUrlMobile : videoUrlDesktop;

          const mobileImage = element?.field_value?.single_image_mobile?.field_value;
          const desktopImage = element?.field_value?.background_image?.field_value;
          const fallbackImage = isMobile && mobileImage ? mobileImage : desktopImage;

          return (
            <SwiperSlide key={index}>
              <div className={classes.heroSlideContainer}>
                <div className={classes.backgroundMediaContainer}>
                  {videoUrl ? (
                    <div className={classes.videoBackground}>
                      <ReactPlayer
                        ref={el => (playerRefs.current[index] = el)}
                        url={videoUrl}
                        playing={indexSwiper === index}
                        muted={isMuted}
                        width="100%"
                        height="100%"
                        loop
                        className={classes.reactPlayer}
                        playsinline
                        config={{
                          youtube: {
                            playerVars: {
                              controls: 0,
                              modestbranding: 1,
                              rel: 0,
                              iv_load_policy: 3,
                              fs: 0,
                              playsinline: 1,
                            },
                          },
                          file: {
                            attributes: {
                              disablePictureInPicture: true,
                              controlsList: 'nodownload',
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <FluidImage className={classes.slideBackground}  {...fallbackImage} />
                  )}
                </div>

                {element?.field_value?.text_scale?.field_value && (
                  <div className={classes.ribbonSection}>
                    <Text variant="bodyM" className="text">
                      {element?.field_value?.text_scale?.field_value}
                    </Text>
                  </div>
                )}

                <Container className={classes.slideContentContainer}>
                  {!isMobile && element?.field_value?.position_center_logo?.field_value && (
                    <div className={`${classes.slideLogo} ${classes.logoCenter}`}>
                      <FluidImage className={`${classes.slideForeground} ${classes.slideLeft}`}
                                  {...element?.field_value?.logo_image?.field_value}
                      />
                    </div>
                  )}

                  <div
                    className={classes.slideDescriptionContainer}
                    style={
                      element?.field_value?.position_center_logo?.field_value && !isMobile
                        ? {top: '300px'}
                        : {}
                    }
                  >
                    {!isMobile && (
                      <div
                        className={`${classes.slideLogo} ${
                          element?.field_value?.narrow_logo_desktop?.field_value ? classes.logoNarrowDesktop : ''
                        } ${!element?.field_value?.new_arrivals_2_layer?.field_value ? classes.noChip : ''}`}
                      >
                        <FluidImage className={`${classes.slideForeground} ${classes.slideLeft} ${
                          element?.field_value?.position_center_logo?.field_value ? classes.dNone : classes.dBlock
                        }`}
                                    {...element?.field_value?.logo_image?.field_value}
                        />
                      </div>
                    )}

                    <Box className={`${classes.dFlex} ${classes.w100}`}
                         style={{flexDirection: 'column', gap: '2px'}}>
                      {item.field_value.length > 1 && (
                        <div
                          className={`${classes.navigationMobile} ${!element?.field_value?.new_arrivals_2_layer?.field_value ? classes.noChip : ''}`}>
                          <div className={classes.navigationText}>
                            <Text variant="bodyM">{currentSlide}</Text> / <Text
                            variant="bodyM">{totalSlides}</Text>
                          </div>
                        </div>
                      )}
                      <div className={classes.slideNew}>
                        {element?.field_value?.new_arrivals_2_layer?.field_value &&
                          <Chip
                            label={element?.field_value?.new_arrivals_2_layer.field_value}
                            className={classes.newArrivals}
                          />}
                      </div>
                    </Box>

                    <div className={`${classes.mb4p} ${isMobile ? classes.textCenter : ''} ${classes.mb0}`}>
                      <a
                        href={element?.field_value?.cta?.field_value?.href}
                        // onClick={() => GTMEvent(info, 'slider', 'click_link', `cta-slider-${element?.field_value?.cta?.field_value?.tracking?.product_name}`)}
                        className={classes.textDecoration}
                      >
                        {info?.template_label === 'Homepage' || !firstModulePage?.includes('HeroBanner') ? (
                          <span className={classes.slideTitle}>
                            {element?.field_value?.title?.field_value}
                          </span>
                        ) : (
                          <h1 className={classes.slideTitle}>
                            {element?.field_value?.title?.field_value}
                          </h1>
                        )}
                      </a>
                    </div>

                    <div
                      className={`${isMobile ? `${classes.mb22p} ${classes.textCenter}` : `${classes.mb4} ${classes.marginIpad}`}`}>
                      <span className={classes.slideSubtitle}>
                        {element?.field_value?.subtitle?.field_value}
                      </span>
                    </div>

                    <CtaButton
                      className={`${classes.ctaButton} ${isMobile ? classes.w100 : ''} ${classes.btnSmall}`}
                      // trackingObj={info}
                      trackingAction="click_link"
                      trackingLabel={`cta-slider-${element?.field_value?.cta?.field_value?.tracking?.product_name}`}
                      href={element?.field_value?.cta?.field_value?.href}
                      next
                      size="large"
                    >
                      {element?.field_value?.cta?.field_value?.label || ''}
                    </CtaButton>
                  </div>

                  {element?.field_value?.legal_price?.field_value && (
                    <div
                      style={{color: element?.field_value?.linecolor?.field_value}}
                      className={isMobile ? classes.legalTextMobile : classes.legalText}
                    >
                      {element?.field_value?.legal_price?.field_value}
                    </div>
                  )}
                </Container>

                {videoUrl && (
                  <Icon
                    className={classes.videoControlBtn}
                    onClick={toggleMute}
                    aria-label="Mute video"
                    name={isMuted ? 'mute' : 'unmute'}
                  />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {isBrowser && item.field_value.length > 1 && (
        <div className={`${classes.navigation} ${classes.dNone} ${classes.dMdFlex}`}>
          <div
            id="heroBannerVideosPrev"
            className={classes.iconBtn}
            onClick={() => {
              GTMEvent(item, 'home_slider', 'click_button', 'swiper-slider');
            }}
          >
            <Icon className={classes.icon24} name="left_white"/>
          </div>
          <div className={classes.navigationText}>
            <Text variant="bodyM">{currentSlide}</Text> / <Text variant="bodyM">{totalSlides}</Text>
          </div>
          <div
            type="button"
            id="heroBannerVideosNext"
            className={`${classes.iconBtn} mr-0 mr-md-3`}
            onClick={() => {
              GTMEvent(item, 'home_slider', 'click_button', 'swiper-slider');
            }}
          >
            <Icon className={classes.icon24} name="right_white"/>
          </div>
        </div>
      )}
    </Module>
  );
};

HeroBannerSlidingVideosModule.propTypes = {
  primary: PropTypes.object.isRequired,
  info: PropTypes.object.isRequired,
};

export default HeroBannerSlidingVideosModule;